import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

function WebsiteInput({ websiteUrl, onWebsiteChange, onBack, onContinue, loading, error, screenshotUrl }) {
  const { t } = useTranslation('fastresearch');
  const [showPreview, setShowPreview] = useState(false);
  const [countdown, setCountdown] = useState(20); // Start at 20 seconds
  const [messageIndex, setMessageIndex] = useState(0);
  const intervalRef = useRef(null);
  const messageIntervalRef = useRef(null);

  const rotatingMessages = t('websiteInput.rotatingMessages', { returnObjects: true });
  const messages = Array.isArray(rotatingMessages) && rotatingMessages.length > 0
    ? rotatingMessages
    : [t('websiteInput.searchingWeb')];

  React.useEffect(() => {
    console.log('[WebsiteInput] screenshotUrl changed:', screenshotUrl);
    if (screenshotUrl) {
      // Show immediately when screenshot is available (even while loading)
      console.log('[WebsiteInput] Setting showPreview = true');
      setShowPreview(true);
    } else {
      console.log('[WebsiteInput] Setting showPreview = false');
      setShowPreview(false);
    }
  }, [screenshotUrl]);

  // Countdown timer effect - shows during search, before screenshot appears
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (loading && !showPreview) {
      setCountdown(20); // Start at 20 seconds for search phase

      intervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 0) {
            // Stop the interval when countdown reaches 0
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [loading, showPreview]);

  // Rotate marketing messages every 3.5s while the overlay is visible
  useEffect(() => {
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }

    if (loading && !showPreview) {
      setMessageIndex(0);
      messageIntervalRef.current = setInterval(() => {
        setMessageIndex(prev => (prev + 1) % messages.length);
      }, 3500);
    }

    return () => {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
    };
  }, [loading, showPreview, messages.length]);

  return (
    <>
      <h1 style={{
        fontSize: "clamp(2rem, 8vw, 2.75rem)",
        marginBottom: "1rem",
        fontWeight: "900",
        color: "#fff",
        letterSpacing: "-0.04em",
        lineHeight: "1",
        textAlign: "center"
      }}>
        {t('websiteInput.title')}
      </h1>

      <p style={{
        fontSize: "clamp(0.9375rem, 3vw, 1rem)",
        color: "#888",
        marginBottom: "2rem",
        lineHeight: "1.5",
        textAlign: "center"
      }}>
        {t('websiteInput.subtitle')}
      </p>

      {error && (
        <div style={{
          padding: "1rem",
          marginBottom: "1.5rem",
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          border: "1px solid rgba(239, 68, 68, 0.3)",
          borderRadius: "10px",
          color: "#f87171",
          fontSize: "0.875rem",
          textAlign: "center"
        }}>
          {error}
        </div>
      )}

      <input
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck="false"
        value={websiteUrl}
        onChange={(e) => onWebsiteChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing && websiteUrl.trim() && !loading) {
            onContinue();
          }
        }}
        placeholder={t('websiteInput.placeholder')}
        disabled={loading}
        style={{
          width: "100%",
          padding: "1.125rem 1.25rem",
          fontSize: "clamp(0.9375rem, 3.5vw, 1rem)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          borderRadius: "10px",
          outline: "none",
          marginBottom: "1rem",
          backgroundColor: loading ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.08)",
          color: "#fff",
          transition: "all 0.3s ease",
          textAlign: "center",
          fontWeight: "600"
        }}
        onFocus={(e) => {
          if (!loading) {
            e.target.style.borderColor = "rgba(99, 102, 241, 0.5)";
            e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
          }
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "rgba(255, 255, 255, 0.2)";
          e.target.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
        }}
      />

      {/* Countdown overlay - shows during search, hides when screenshot appears */}
      {loading && !showPreview && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          backdropFilter: "blur(2px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          animation: "fadeIn 0.3s ease"
        }}>
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem"
          }}>
            {/* Compact animated particle container */}
            <div style={{
              position: "relative",
              width: "120px",
              height: "120px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              {/* Countdown number in center */}
              <div style={{
                position: "absolute",
                fontSize: "1.5rem",
                fontWeight: "700",
                color: "#a78bfa",
                textAlign: "center",
                lineHeight: "1",
                zIndex: 10
              }}>
                {countdown}
                <div style={{
                  fontSize: "0.5rem",
                  fontWeight: "600",
                  color: "rgba(167, 139, 250, 0.6)",
                  marginTop: "2px",
                  letterSpacing: "0.5px"
                }}>
                  {t('websiteInput.sec')}
                </div>
              </div>

              {/* Outer rotating ring */}
              <div style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                border: "2px solid rgba(99, 102, 241, 0.3)",
                borderRadius: "50%",
                animation: "spin 3s linear infinite"
              }} />

              {/* Middle rotating ring (opposite direction) */}
              <div style={{
                position: "absolute",
                width: "80px",
                height: "80px",
                border: "2px solid rgba(167, 139, 250, 0.4)",
                borderTopColor: "#a78bfa",
                borderRadius: "50%",
                animation: "spinReverse 2s linear infinite"
              }} />

              {/* Particles flowing in */}
              {[...Array(8)].map((_, i) => (
                <div key={i} style={{
                  position: "absolute",
                  width: "4px",
                  height: "4px",
                  backgroundColor: "#6366f1",
                  borderRadius: "50%",
                  boxShadow: "0 0 12px #6366f1",
                  animation: `particle${i % 4} 2s ease-in-out infinite`,
                  animationDelay: `${i * 0.25}s`,
                  opacity: 0
                }} />
              ))}
            </div>

            {/* Loading text */}
            <div style={{ textAlign: "center", minHeight: "2.5rem" }}>
              <p
                key={messageIndex}
                style={{
                  fontSize: "clamp(0.9375rem, 3vw, 1rem)",
                  fontWeight: "700",
                  color: "#fff",
                  marginBottom: 0,
                  animation: "fadeIn 0.4s ease"
                }}
              >
                {messages[messageIndex]}
              </p>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={onContinue}
        disabled={!websiteUrl.trim() || loading}
        style={{
          width: "100%",
          padding: "clamp(1rem, 3vh, 1.25rem)",
          fontSize: "clamp(0.9375rem, 3.5vw, 1rem)",
          fontWeight: "800",
          border: "none",
          borderRadius: "10px",
          backgroundColor: (websiteUrl.trim() && !loading) ? "#fff" : "rgba(255, 255, 255, 0.1)",
          color: (websiteUrl.trim() && !loading) ? "#000" : "#555",
          cursor: (websiteUrl.trim() && !loading) ? "pointer" : "not-allowed",
          transition: "all 0.3s ease",
          letterSpacing: "0.02em"
        }}
        onMouseEnter={(e) => {
          if (websiteUrl.trim() && !loading) {
            e.currentTarget.style.backgroundColor = "#a78bfa";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = (websiteUrl.trim() && !loading) ? "#fff" : "rgba(255, 255, 255, 0.1)";
        }}
      >
        {loading ? (
          showPreview ? t('websiteInput.scanning') : t('websiteInput.searching')
        ) : (
          t('websiteInput.continue')
        )}
      </button>

      {/* AI Screenshot Preview - shown below buttons */}
      {showPreview && screenshotUrl && (
        <div style={{
          marginTop: "1.5rem",
          borderRadius: "10px",
          overflow: "hidden",
          position: "relative",
          border: "2px solid #6366f1",
          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)"
        }}>
          <img
            src={screenshotUrl}
            alt={t('websiteInput.websitePreview')}
            style={{
              width: "100%",
              height: "160px",
              objectFit: "cover",
              objectPosition: "top",
              display: "block"
            }}
          />

          {/* Scanning overlay */}
          <div style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none"
          }}>
            {/* Scanning Line */}
            <div style={{
              position: "absolute",
              left: 0,
              right: 0,
              height: "2px",
              background: "linear-gradient(90deg, transparent, #6366f1, transparent)",
              boxShadow: "0 0 10px #6366f1",
              animation: "scanLine 3s ease-in-out infinite"
            }} />

            {/* Grid Overlay */}
            <div style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `
                linear-gradient(rgba(99, 102, 241, 0.15) 1px, transparent 1px),
                linear-gradient(90deg, rgba(99, 102, 241, 0.15) 1px, transparent 1px)
              `,
              backgroundSize: "24px 24px"
            }} />

            {/* Corner Brackets */}
            {['tl', 'tr', 'bl', 'br'].map(pos => (
              <div key={pos} style={{
                position: "absolute",
                width: "16px",
                height: "16px",
                ...(pos.includes('t') ? { top: "6px" } : { bottom: "6px" }),
                ...(pos.includes('l') ? { left: "6px" } : { right: "6px" }),
                borderTop: pos.includes('t') ? "2px solid #6366f1" : "none",
                borderBottom: pos.includes('b') ? "2px solid #6366f1" : "none",
                borderLeft: pos.includes('l') ? "2px solid #6366f1" : "none",
                borderRight: pos.includes('r') ? "2px solid #6366f1" : "none"
              }} />
            ))}

            {/* AI Badge */}
            <div style={{
              position: "absolute",
              bottom: "8px",
              right: "8px",
              padding: "5px 10px",
              background: "rgba(99, 102, 241, 0.95)",
              backdropFilter: "blur(10px)",
              color: "white",
              fontSize: "10px",
              fontWeight: "700",
              borderRadius: "5px",
              letterSpacing: "0.5px",
              boxShadow: "0 2px 8px rgba(99, 102, 241, 0.4)"
            }}>
              {t('websiteInput.aiAnalyzing')}
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes scanLine {
            0% { top: 0; opacity: 0; }
            50% { opacity: 1; }
            100% { top: 100%; opacity: 0; }
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes spinReverse {
            0% { transform: rotate(360deg); }
            100% { transform: rotate(0deg); }
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.15); opacity: 0.8; }
          }

          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }

          @keyframes particle0 {
            0% {
              transform: translate(-60px, -60px) scale(0);
              opacity: 0;
            }
            50% { opacity: 1; }
            100% {
              transform: translate(0, 0) scale(1);
              opacity: 0;
            }
          }

          @keyframes particle1 {
            0% {
              transform: translate(60px, -60px) scale(0);
              opacity: 0;
            }
            50% { opacity: 1; }
            100% {
              transform: translate(0, 0) scale(1);
              opacity: 0;
            }
          }

          @keyframes particle2 {
            0% {
              transform: translate(60px, 60px) scale(0);
              opacity: 0;
            }
            50% { opacity: 1; }
            100% {
              transform: translate(0, 0) scale(1);
              opacity: 0;
            }
          }

          @keyframes particle3 {
            0% {
              transform: translate(-60px, 60px) scale(0);
              opacity: 0;
            }
            50% { opacity: 1; }
            100% {
              transform: translate(0, 0) scale(1);
              opacity: 0;
            }
          }
        `}
      </style>
    </>
  );
}

export default WebsiteInput;
